import StringUtils from '@/utils/StringUtils.js';
import RcDomainObject from "../../../common/RcDomainObject";
import Company from "../../company/Company.js";

/**
 * The latest value recorded against an asset for one thing that is measured on
 * it - the odometer, a forklift's hour meter, a generator's run hours.
 *
 * One row per asset per measured thing, identified by its Key. The inspection
 * results remain the record of what was answered; this is the latest-value
 * lookup that lets an inspection compare a new reading against the one before
 * it without walking the asset's inspection history.
 */
export default class VehicleData extends RcDomainObject {

  static MODEL_NAME = 'VehicleData';

  static Fields = {
    Key: "key",
    Value: "value",
    RecordedOn: "recordedOn",
    Vehicle: "Vehicle",
    Company: Company.MODEL_NAME,
  }

  static Values = {
    Keys: {
      // The asset's odometer, the one key that is not a checklist item id.
      Odometer: "odometer",
    }
  }

  constructor(domain, data) {
    super(domain, data, VehicleData.MODEL_NAME);
  }

  fields() {
    return VehicleData.Fields;
  }

  find() {
    var result = this.domain.vehicles().vehicleData().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    return result;
  }

  key() {
    return this.getString(VehicleData.Fields.Key);
  }

  withKey(key) {
    this.put(VehicleData.Fields.Key, key);
    return this;
  }

  isKey(key) {
    return StringUtils.equals(key, this.key());
  }

  isOdometer() {
    return this.isKey(VehicleData.Values.Keys.Odometer);
  }

  withKeyOdometer() {
    return this.withKey(VehicleData.Values.Keys.Odometer);
  }

  value() {
    return this.getString(VehicleData.Fields.Value);
  }

  withValue(value) {
    this.put(VehicleData.Fields.Value, StringUtils.toString(value));
    return this;
  }

  hasValue() {
    return StringUtils.isNotEmpty(this.value());
  }

  // The value as a number, or NaN when nothing usable is recorded. Callers
  // comparing readings should check for NaN rather than trusting a 0.
  numberValue() {
    return parseFloat(this.value());
  }

  recordedOn() {
    var value = parseInt(this.get(VehicleData.Fields.RecordedOn));
    return isNaN(value) ? 0 : value;
  }

  withRecordedOn(recordedOn) {
    this.put(VehicleData.Fields.RecordedOn, recordedOn);
    return this;
  }

  isNewerThan(recordedOn) {
    return this.recordedOn() > recordedOn;
  }

  company() {
    var data = this.get(VehicleData.Fields.Company);
    return new Company(this.domain, data);
  }

  withCompany(company) {
    this.put(VehicleData.Fields.Company, company);
    return this;
  }

  copy() {
    var data = this._copy();
    return new VehicleData(this.domain, data);
  }
}
